import { useEffect, useState } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { ClimbingBoxLoader } from "react-spinners";

import {
  doc,
  addDoc,
  collection,
  updateDoc,
  serverTimestamp,
  getDoc,
  arrayUnion,
} from "firebase/firestore";

import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";

import {
  QuizAnswerBox,
  QuestionContainer,
  QuestionHeader,
  QuestionBody,
  ProgressBarContainer,
  ProgressBarProgress,
} from "../components/theme/QuizBox";

import {
  D1,
  GradientSpan,
  P1,
  P2,
  P3,
} from "../components/theme/text/Headings";

import { auth, db } from "../services/firebase.config";
import {
  gradientColors,
  White05,
  Black,
  OffWhite,
} from "../components/theme/colors";
import LayoutQuiz from "../components/layout/QuizLayouts/layoutQuiz";
import dataCache from "../services/DataCache";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";
import { refreshMixpanelUserProperties } from "../services/AuthContext";
import remoteQuizService from "../services/RemoteQuizService";

import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";

/**
 * Get gradient config, supporting both static color references and hex strings from Firestore
 * @param {Object} quizConfig - Quiz configuration
 * @returns {Object} - Gradient config with background and transitionBackground
 */
const getGradientConfig = (quizConfig) => {
  // Try static gradient colors first
  if (gradientColors[quizConfig.quizType]) {
    return gradientColors[quizConfig.quizType];
  }

  // Fall back to config's gradientColors (from Firestore)
  if (quizConfig.gradientColors) {
    return quizConfig.gradientColors;
  }

  // Default fallback
  return {
    background: "#3B96FF",
    transitionBackground: "#F951FF",
  };
};

const UniversalQuestionPage = ({ quizType }) => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [searchParams] = useSearchParams();
  const sharedId = searchParams.get("shared");
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizDocId, setQuizDocId] = useState(null);

  // State for remote quiz data
  const [remoteQuizConfig, setRemoteQuizConfig] = useState(null);
  const [remoteQuestionsData, setRemoteQuestionsData] = useState(null);
  const [isLoadingRemote, setIsLoadingRemote] = useState(true);

  const location = useLocation();

  // Fetch remote quiz config and questions on mount
  useEffect(() => {
    const fetchRemoteData = async () => {
      try {
        console.log("📥 Fetching remote data for quiz:", quizType);

        // Try to get remote config
        const config = await remoteQuizService.getQuizConfig(quizType);
        if (config) {
          console.log("✅ Config loaded:", config.quizName);
          setRemoteQuizConfig(config);
        } else {
          console.warn("⚠️ No config found for:", quizType);
        }

        // Try to get remote questions
        const questions = await remoteQuizService.getQuizQuestions(quizType);
        if (questions) {
          console.log("✅ Questions loaded:", questions.length, "questions");
          console.log(
            "📋 Question IDs in Firestore:",
            questions.map((q) => q.id).join(", ")
          );
          setRemoteQuestionsData(questions);
        } else {
          console.warn("⚠️ No questions found for:", quizType);
        }
      } catch (error) {
        console.error("❌ Error fetching remote data:", error);
      } finally {
        setIsLoadingRemote(false);
      }
    };

    fetchRemoteData();
  }, [quizType]);

  // Get the quiz config from remote
  const quizConfig = remoteQuizConfig;

  // Get questions data from remote
  const questionsData = remoteQuestionsData;

  // Find the specific question (convert both to strings for comparison in case of type mismatch)
  const questionData = questionsData?.find(
    (q) => String(q.id) === String(questionId)
  );

  // Debug logging for question lookup
  console.log("🔍 Looking for question:", {
    questionId,
    questionIdType: typeof questionId,
    questionsCount: questionsData?.length,
    foundQuestion: !!questionData,
    questionDataId: questionData?.id,
  });

  // HOOKS MUST BE CALLED FIRST - before any conditional returns
  // Reset selection when navigating between questions
  useEffect(() => {
    setSelectedQuiz(null);
  }, [location.pathname]);

  // Initialize quiz document
  useEffect(() => {
    // Only initialize if we have valid config and question data
    if (!quizConfig || !questionData) return;
    const initializeQuizDoc = async () => {
      if (!user) {
        alert("You need to be logged in to start the quiz.");
        return;
      }

      const userProfileRef = doc(db, "public-profiles", user.uid);
      const userProfileDoc = await getDoc(userProfileRef);

      const canSaveToProfile =
        quizConfig.databaseField && quizConfig.saveToProfile !== false;

      if (
        canSaveToProfile &&
        userProfileDoc.exists() &&
        userProfileDoc.data()[quizConfig.databaseField]
      ) {
        setQuizDocId(userProfileDoc.data()[quizConfig.databaseField]);
      } else {
        if (!quizDocId) {
          // Create a new quiz document only if `quizDocId` is not set
          const newQuizRef = await addDoc(collection(db, "quiz-answers"), {
            uid: user.uid,
            answers: {},
            created: serverTimestamp(),
            quizName: quizConfig.quizName,
          });
          setQuizDocId(newQuizRef.id);

          // Optionally save the new `quizDocId` to the user's profile document (core quizzes only)
          if (canSaveToProfile) {
            await updateDoc(userProfileRef, {
              [quizConfig.databaseField]: newQuizRef.id,
            });
          }
        }
      }
    };

    initializeQuizDoc();
  }, [
    user,
    quizDocId,
    quizConfig?.databaseField,
    quizConfig?.quizName,
    quizConfig,
    questionData,
  ]);

  // Show loading state while fetching remote data
  if (isLoadingRemote) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: Black,
        }}
      >
        <ClimbingBoxLoader color={OffWhite} />
      </div>
    );
  }

  // Error handling for missing data - AFTER hooks
  if (!quizConfig) {
    return null;
  }

  if (!questionsData) {
    return null;
  }

  if (!questionData) {
    return null;
  }

  // Handle back navigation
  const handleBackNavigation = () => {
    const currentQuestionNumber = parseInt(questionId);
    const previousQuestionNumber = currentQuestionNumber - 1;

    if (previousQuestionNumber >= 1) {
      // Navigate to previous question using dynamic route format
      const baseQuestionRoute = quizConfig.firstQuestionRoute.replace("/1", "");
      const previousUrl = sharedId
        ? `${baseQuestionRoute}/${previousQuestionNumber}?shared=${sharedId}`
        : `${baseQuestionRoute}/${previousQuestionNumber}`;
      navigate(previousUrl);
    }
  };

  // Handle answer selection and navigation
  const handleSelectQuiz = async (answer, percentage) => {
    try {
      console.log("🎯 handleSelectQuiz called:", {
        questionId,
        answer,
        percentage,
        currentQuestionData: questionData,
        nextQuestionId: questionData?.nextQuestionId,
        quizDocId,
        userId: user?.uid,
      });

      // Platform-safe haptics - prevents Capacitor errors on web
      if (Capacitor.getPlatform() !== "web") {
        await Haptics.impact({ style: ImpactStyle.Light });
      }

      if (!quizDocId) {
        console.error("❌ No quizDocId available");
        return;
      }

      if (selectedQuiz === answer) {
        // Deselect the current answer
        setSelectedQuiz(null);
        return;
      }

      // Select answer + disable hover glitch
      setSelectedQuiz(answer);
      document.body.style.pointerEvents = "none"; // 🛑 stop all hover effects

      setTimeout(() => {
        document.body.style.pointerEvents = "auto"; // ✅ re-enable after nav
      }, 500); // long enough for route change to complete

      if (!user) {
        alert("You need to be logged in to submit an answer.");
        return;
      }

      const quizDocRef = doc(db, "quiz-answers", quizDocId);

      // Determine if this is the final question
      const isFinalQuestion = !questionData.nextQuestionId;
      console.log("📋 Question state:", {
        isFinalQuestion,
        nextQuestionId: questionData.nextQuestionId,
        questionField: questionData.field,
      });

      try {
        // Update the Firestore document
        await updateDoc(quizDocRef, {
          [`answers.${questionData.id}`]: {
            answer,
            percentage,
            field: questionData.field,
          },
          ...(isFinalQuestion && { status: "completed" }), // Mark the quiz as completed if it's the final question
        });

        // CACHE INVALIDATION: Clear relevant caches when quiz is completed
        if (isFinalQuestion) {
          console.log("🔄 Quiz completed - invalidating home page cache");

          // Track quiz completion in Mixpanel and PostHog for daily analytics
          const quizCompletionProps = {
            quiz_type: quizType,
            quiz_name: quizConfig.title,
            user_id: user.uid,
            quiz_id: quizDocId,
            shared_quiz: !!sharedId,
            shared_id: sharedId,
            completion_date: new Date().toISOString().split("T")[0], // YYYY-MM-DD format for daily queries
            completion_time: new Date().toISOString(),
            timestamp: new Date().toISOString(),
          };
          mixpanelService.track("Quiz Completed", quizCompletionProps);
          postHogService.track("Quiz Completed", quizCompletionProps);

          // Refresh user properties in Mixpanel and PostHog to update quiz completion status
          refreshMixpanelUserProperties(user.uid);

          // Map quiz types to cache key suffixes
          const quizTypeToCacheKey = {
            "best-date": "fd",
            "political-alignment": "pa",
            "personality-check": "mb",
            "sex-styles": "ss",
            "am-i-gay": "ag",
            "affection-profile": "ap",
            "love-languages": "ll",
          };

          const cacheKeySuffix = quizTypeToCacheKey[quizConfig.quizType];

          if (cacheKeySuffix) {
            // Invalidate the specific quiz completion cache
            dataCache.invalidate(
              `quiz-completion-${cacheKeySuffix}-${user.uid}`
            );

            // Also invalidate the home data cache to force a full refresh
            dataCache.invalidate(`home-data-${user.uid}`);

            // Invalidate user profile cache in case it needs updating
            dataCache.invalidate(`user-profile-${user.uid}`);

            // Invalidate profile insights cache since quiz completion affects comprehensive insights
            dataCache.invalidate(`profile-insights-${user.uid}`);

            console.log(
              `✅ Cache invalidated for quiz type: ${quizConfig.quizType}`
            );
          }
        }

        // Handle shared quiz updates (only if sharedId exists)
        if (isFinalQuestion && sharedId) {
          const sharedDocRef = doc(db, "shared-quizzes", sharedId);

          // Update the shared quiz document with the receiver's quiz ID
          await updateDoc(sharedDocRef, {
            receiverQuizId: quizDocId,
            receiverUid: user.uid,
            completed: true,
            completedAt: serverTimestamp(),
          });

          // Update shared quizzes for sender and receiver
          const sharedDoc = await getDoc(sharedDocRef);
          if (sharedDoc.exists()) {
            const { senderUid, receiverUid } = sharedDoc.data();
            const senderProfileRef = doc(db, "public-profiles", senderUid);
            const receiverProfileRef = doc(db, "public-profiles", receiverUid);

            await Promise.all([
              updateDoc(senderProfileRef, {
                shared: arrayUnion(sharedId),
              }),
              updateDoc(receiverProfileRef, {
                shared: arrayUnion(sharedId),
              }),
            ]);

            // Invalidate profile caches for both users since shared array changed
            dataCache.invalidate(`user-profile-${senderUid}`);
            dataCache.invalidate(`user-profile-${receiverUid}`);
          }
        }

        // Navigate to the next question or results
        if (isFinalQuestion) {
          // Handle special web logic for anonymous users
          if (quizConfig.hasSpecialWebLogic) {
            const isWeb = Capacitor.getPlatform() === "web";
            const isAnonymous = user?.isAnonymous;

            if (isWeb && isAnonymous) {
              // Only anonymous users on web should go to save account
              navigate("/save-account", { state: { linkAnonymous: true } });
              return;
            }
          }

          // Standard navigation to results
          const nextUrl = sharedId
            ? `${quizConfig.resultsRoute}?shared=${sharedId}`
            : quizConfig.resultsRoute;
          navigate(nextUrl);
        } else {
          // Navigate to next question using dynamic route format
          const baseQuestionRoute = quizConfig.firstQuestionRoute.replace(
            "/1",
            ""
          );
          const nextUrl = sharedId
            ? `${baseQuestionRoute}/${questionData.nextQuestionId}?shared=${sharedId}`
            : `${baseQuestionRoute}/${questionData.nextQuestionId}`;
          console.log("🔀 Navigating to next question:", {
            baseQuestionRoute,
            nextQuestionId: questionData.nextQuestionId,
            nextUrl,
          });
          navigate(nextUrl);
        }
      } catch (error) {
        console.error("❌ Error updating quiz in Firestore:", error);
        console.error("Error details:", {
          message: error.message,
          code: error.code,
          stack: error.stack,
        });
        alert(`Error saving answer: ${error.message}`);
      }
    } catch (outerError) {
      console.error("❌ Outer error in handleSelectQuiz:", outerError);
      console.error("Outer error details:", {
        message: outerError.message,
        stack: outerError.stack,
      });
      alert(`Error: ${outerError.message}`);
      document.body.style.pointerEvents = "auto"; // Re-enable interactions
    }
  };

  // Calculate progress percentage
  const progressPercent = (questionData.id / quizConfig.totalQuestions) * 100;
  const gradientConfig = getGradientConfig(quizConfig);

  // Check if we can show back arrow (not on first question)
  const canGoBack = parseInt(questionId) > 1;

  // Create quiz header content to pass as prop
  const quizHeaderContent = (
    <>
      <ProgressBarContainer>
        <ProgressBarProgress
          $background={gradientConfig.background}
          $transitionBackground={gradientConfig.transitionBackground}
          width={`${progressPercent}%`}
        />
      </ProgressBarContainer>
      <Container>
        <Row style={{ position: "relative", alignItems: "center" }}>
          {canGoBack && (
            <div
              style={{
                position: "absolute",
                left: "20px",
                zIndex: 1,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
              }}
              onClick={handleBackNavigation}
            >
              <ArrowCircleLeftIcon
                style={{
                  color: White05,
                  fontSize: "28px",
                }}
              />
            </div>
          )}
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ offset: 1, span: 10 }}
            className="text-align-center"
          >
            <P3
              className="mb-0 justify-content-center"
              style={{ fontWeight: "700" }}
              color={White05}
            >
              {quizConfig.headerText}
            </P3>
          </Col>
        </Row>
      </Container>
    </>
  );

  return (
    <LayoutQuiz quizHeader={quizHeaderContent}>
      <Container>
        <Row className="mt-10">
          <Col
            key={questionData.id}
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ offset: 1, span: 10 }}
            className="text-align-center"
          >
            <QuestionContainer className="mt-20 mb-20">
              <QuestionHeader>
                <P2 style={{ fontWeight: "600" }}>
                  <GradientSpan
                    $background={gradientConfig.background}
                    $transitionBackground={gradientConfig.transitionBackground}
                  >
                    Question {questionData.id} out of{" "}
                    {quizConfig.totalQuestions}{" "}
                  </GradientSpan>
                </P2>
              </QuestionHeader>
              <QuestionBody>
                <D1>{questionData.questionText}</D1>
              </QuestionBody>
            </QuestionContainer>

            {questionData.answers.map((answer, index) => (
              <Row key={index}>
                <Col>
                  <QuizAnswerBox
                    $background={gradientConfig.background}
                    $transitionBackground={gradientConfig.transitionBackground}
                    $selected={selectedQuiz === answer.copy}
                    $anyselected={selectedQuiz !== null}
                    onClick={() =>
                      handleSelectQuiz(answer.answer, answer.percentage)
                    }
                  >
                    <P1 className="mb-0">{answer.copy}</P1>
                  </QuizAnswerBox>
                </Col>
              </Row>
            ))}
          </Col>
        </Row>
      </Container>
    </LayoutQuiz>
  );
};

export default UniversalQuestionPage;
